"use client";

import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "ellaandil:recent-searches";
const MAX_RECENT = 5;

const readStored = (): string[] => {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
	} catch {
		return [];
	}
};

const writeStored = (values: string[]): void => {
	if (typeof window === "undefined") return;
	try {
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
	} catch {
		// quota / privacy mode — silently drop
	}
};

export const useRecentSearches = () => {
	const [recent, setRecent] = useState<string[]>([]);

	useEffect(() => {
		setRecent(readStored());
	}, []);

	const add = useCallback((term: string) => {
		const trimmed = term.trim();
		if (!trimmed) return;
		setRecent((prev) => {
			const next = [
				trimmed,
				...prev.filter((t) => t.toLowerCase() !== trimmed.toLowerCase()),
			].slice(0, MAX_RECENT);
			writeStored(next);
			return next;
		});
	}, []);

	const remove = useCallback((term: string) => {
		setRecent((prev) => {
			const next = prev.filter((t) => t !== term);
			writeStored(next);
			return next;
		});
	}, []);

	const clear = useCallback(() => {
		setRecent([]);
		writeStored([]);
	}, []);

	return { recent, add, remove, clear };
};
